import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { recruitingAPI, jobsAPI } from '../lib/api';
import {
  Users, Mail, Phone, FileText, Star, Trash2, MoreVertical, UserPlus,
  ArrowRight, Download, Loader2, Inbox, Filter,
  Briefcase, Clock, CheckCircle2, TrendingUp,
  Gift, Euro, Save,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const STAGE_COLORS = {
  neu: 'border-t-blue-500',
  sichtung: 'border-t-amber-500',
  vorstellungsgespraech: 'border-t-violet-500',
  zusage: 'border-t-emerald-500',
  absage: 'border-t-rose-500',
};

const fmt = (d) => { try { return format(parseISO(d), 'dd.MM.yyyy', { locale: de }); } catch { return '—'; } };

export default function Recruiting() {
  const { hasPermission, isAdmin } = useAuth();
  const navigate = useNavigate();
  const canEdit = isAdmin || hasPermission('job_postings', 'edit');

  const [loading, setLoading] = useState(true);
  const [stages, setStages] = useState([]);
  const [labels, setLabels] = useState({});
  const [apps, setApps] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [jobFilter, setJobFilter] = useState('all');
  const [detail, setDetail] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [convertOpen, setConvertOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [refOpen, setRefOpen] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [refSettings, setRefSettings] = useState({ bonus_amount: 0, currency: 'EUR', enabled: true });
  const [bonusInput, setBonusInput] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(async () => {
    try {
      const [metaRes, appsRes, jobsRes, statsRes] = await Promise.all([
        recruitingAPI.getMeta(),
        recruitingAPI.listApplications(),
        jobsAPI.getAll(),
        recruitingAPI.getStats().catch(() => ({ data: null })),
      ]);
      setStages(metaRes.data.stages || []);
      setLabels(metaRes.data.labels || {});
      setApps(appsRes.data || []);
      setJobs(jobsRes.data || []);
      setStats(statsRes.data);
    } catch {
      toast.error('Bewerbungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = apps.filter(a => jobFilter === 'all' || a.job_id === jobFilter);

  const moveTo = async (app, status) => {
    try {
      const { data } = await recruitingAPI.setStatus(app.id, status);
      setApps(prev => prev.map(a => a.id === app.id ? data : a));
      if (detail?.id === app.id) setDetail(data);
      toast.success(`Verschoben nach „${labels[status]}"`);
    } catch { toast.error('Statuswechsel fehlgeschlagen'); }
  };

  const setRating = async (app, rating) => {
    try {
      const { data } = await recruitingAPI.updateApplication(app.id, { rating });
      setApps(prev => prev.map(a => a.id === app.id ? data : a));
      if (detail?.id === app.id) setDetail(data);
    } catch { toast.error('Bewertung fehlgeschlagen'); }
  };

  const addNote = async () => {
    const text = noteText.trim();
    if (!text || !detail) return;
    try {
      const { data } = await recruitingAPI.addNote(detail.id, text);
      const updated = { ...detail, notes: [...(detail.notes || []), data] };
      setDetail(updated);
      setApps(prev => prev.map(a => a.id === detail.id ? updated : a));
      setNoteText('');
    } catch { toast.error('Notiz konnte nicht gespeichert werden'); }
  };

  const removeApp = async (app) => {
    if (!window.confirm(`Bewerbung von ${app.first_name} ${app.last_name} wirklich löschen?`)) return;
    try {
      await recruitingAPI.deleteApplication(app.id);
      setApps(prev => prev.filter(a => a.id !== app.id));
      if (detail?.id === app.id) setDetail(null);
      toast.success('Bewerbung gelöscht');
    } catch { toast.error('Löschen fehlgeschlagen'); }
  };

  const convert = async () => {
    if (!detail) return;
    setConverting(true);
    try {
      const { data } = await recruitingAPI.convert(detail.id, {});
      toast.success('Bewerber als Anwärter übernommen');
      setConvertOpen(false);
      setDetail(null);
      await load();
      if (data.employee_id) navigate(`/employees/${data.employee_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Übernahme fehlgeschlagen');
    } finally { setConverting(false); }
  };

  const openReferrals = async () => {
    setRefOpen(true);
    try {
      const { data } = await recruitingAPI.listReferrals();
      setReferrals(data.referrals || []);
      setRefSettings(data.settings || { bonus_amount: 0, currency: 'EUR', enabled: true });
      setBonusInput(String(data.settings?.bonus_amount ?? 0));
    } catch { toast.error('Empfehlungen konnten nicht geladen werden'); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await recruitingAPI.updateReferralSettings({
        bonus_amount: parseFloat(bonusInput) || 0,
        currency: refSettings.currency || 'EUR',
        enabled: refSettings.enabled !== false,
      });
      setRefSettings(data);
      toast.success('Prämie gespeichert');
    } catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setSavingSettings(false); }
  };

  const payout = async (r) => {
    try {
      await recruitingAPI.markPayout(r.id);
      setReferrals(prev => prev.map(x => x.id === r.id ? { ...x, referral_bonus_status: 'ausgezahlt' } : x));
      toast.success('Als ausgezahlt markiert');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Aktion fehlgeschlagen'); }
  };

  const Stars = ({ value, onChange, size = 'h-4 w-4' }) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" onClick={(e) => { e.stopPropagation(); onChange?.(n === value ? 0 : n); }}
          disabled={!onChange} className={onChange ? 'cursor-pointer' : 'cursor-default'}
          data-testid={onChange ? `rate-${n}` : undefined}>
          <Star className={`${size} ${n <= (value || 0) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40'}`} />
        </button>
      ))}
    </div>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="recruiting-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading font-bold text-xl sm:text-3xl text-foreground flex items-center gap-2">
              <Users className="h-6 w-6 text-primary" /> Bewerber
            </h1>
            <p className="text-muted-foreground mt-1">{visible.length} Bewerbung{visible.length === 1 ? '' : 'en'} in der Pipeline</p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button variant="outline" onClick={openReferrals} data-testid="open-referrals-btn">
              <Gift className="h-4 w-4 mr-2" /> Empfehlungen
            </Button>
            <Filter className="h-4 w-4 text-muted-foreground shrink-0" />
            <Select value={jobFilter} onValueChange={setJobFilter}>
              <SelectTrigger className="w-full sm:w-56" data-testid="job-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Stellen</SelectItem>
                {jobs.map(j => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* KPI-Dashboard */}
        {stats && (
          <div className="space-y-4" data-testid="recruiting-kpis">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { key: 'open_jobs', label: 'Offene Stellen', value: stats.open_jobs, icon: Briefcase, tint: 'text-blue-600 bg-blue-500/10' },
                { key: 'total', label: 'Bewerbungen', value: stats.total, icon: Users, tint: 'text-violet-600 bg-violet-500/10' },
                { key: 'hired', label: 'Eingestellt', value: stats.hired, icon: CheckCircle2, tint: 'text-emerald-600 bg-emerald-500/10' },
                { key: 'tth', label: 'Ø Time-to-Hire', value: stats.avg_time_to_hire_days != null ? `${stats.avg_time_to_hire_days} Tage` : '—', icon: Clock, tint: 'text-amber-600 bg-amber-500/10' },
              ].map(k => (
                <Card key={k.key} data-testid={`kpi-${k.key}`}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${k.tint}`}>
                      <k.icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground truncate">{k.label}</p>
                      <p className="text-xl font-bold truncate">{k.value}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Bewerber pro Stelle */}
              <Card data-testid="kpi-per-job">
                <CardContent className="p-4">
                  <p className="text-sm font-semibold mb-3 flex items-center gap-1.5"><TrendingUp className="h-4 w-4 text-primary" /> Bewerber pro Stelle</p>
                  {(stats.per_job || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground">Noch keine Daten</p>
                  ) : (
                    <div className="space-y-2">
                      {stats.per_job.map(j => {
                        const max = stats.per_job[0].count || 1;
                        return (
                          <div key={j.job_id} className="space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="truncate mr-2">{j.title}</span>
                              <span className="font-medium shrink-0">{j.count}</span>
                            </div>
                            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                              <div className="h-full bg-primary rounded-full" style={{ width: `${(j.count / max) * 100}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Herkunftsquellen */}
              <Card data-testid="kpi-sources">
                <CardContent className="p-4">
                  <p className="text-sm font-semibold mb-3 flex items-center gap-1.5"><Filter className="h-4 w-4 text-primary" /> Herkunftsquellen</p>
                  {(stats.by_source || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground">Noch keine Daten</p>
                  ) : (
                    <div className="space-y-2">
                      {stats.by_source.map(s => (
                        <div key={s.source} className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{s.source}</span>
                          <Badge variant="secondary">{s.count}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-64 rounded-xl" />)}
          </div>
        ) : visible.length === 0 ? (
          <Card><CardContent className="p-12 text-center text-muted-foreground">
            <Inbox className="h-10 w-10 mx-auto mb-3 opacity-40" />
            Noch keine Bewerbungen. Veröffentliche eine Stelle extern, damit Bewerbungen über die Karriereseite eingehen.
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="pipeline-board">
            {stages.map(stage => {
              const col = visible.filter(a => a.status === stage);
              return (
                <div key={stage} className="flex flex-col" data-testid={`column-${stage}`}>
                  <div className="flex items-center justify-between mb-2 px-1">
                    <span className="text-sm font-semibold">{labels[stage]}</span>
                    <Badge variant="secondary" className="text-xs">{col.length}</Badge>
                  </div>
                  <div className="space-y-3 flex-1">
                    {col.map(app => (
                      <Card key={app.id}
                        className={`border-t-4 ${STAGE_COLORS[stage] || ''} cursor-pointer hover:shadow-md transition-shadow`}
                        onClick={() => setDetail(app)}
                        data-testid={`app-card-${app.id}`}>
                        <CardContent className="p-3 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{app.first_name} {app.last_name}</p>
                              <p className="text-xs text-muted-foreground truncate">{app.job_title}</p>
                            </div>
                            {canEdit && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" data-testid={`app-menu-${app.id}`}>
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                  {stages.filter(s => s !== stage).map(s => (
                                    <DropdownMenuItem key={s} onClick={() => moveTo(app, s)}>
                                      <ArrowRight className="mr-2 h-3.5 w-3.5" /> {labels[s]}
                                    </DropdownMenuItem>
                                  ))}
                                  <DropdownMenuItem className="text-destructive" onClick={() => removeApp(app)}>
                                    <Trash2 className="mr-2 h-3.5 w-3.5" /> Löschen
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                          <Stars value={app.rating} onChange={canEdit ? (r) => setRating(app, r) : undefined} size="h-3.5 w-3.5" />
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {app.cv_storage_path && <FileText className="h-3.5 w-3.5" title="Lebenslauf vorhanden" />}
                            <span>{fmt(app.created_at)}</span>
                            {app.converted_employee_id && <Badge variant="outline" className="text-[10px] py-0">Anwärter</Badge>}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    {col.length === 0 && <div className="text-xs text-muted-foreground/60 text-center py-6 border border-dashed rounded-lg">leer</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail-Sheet */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="app-detail">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center justify-between gap-2 pr-6">
                  <span>{detail.first_name} {detail.last_name}</span>
                  <Badge variant="secondary">{labels[detail.status]}</Badge>
                </SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-5 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Stelle</p>
                  <p className="font-medium">{detail.job_title}</p>
                  <p className="text-xs text-muted-foreground mt-1">Quelle: {detail.source} · Eingegangen {fmt(detail.created_at)}</p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <a href={`mailto:${detail.email}`} className="inline-flex items-center gap-1.5 text-primary hover:underline"><Mail className="h-4 w-4" /> {detail.email}</a>
                  {detail.phone && <a href={`tel:${detail.phone}`} className="inline-flex items-center gap-1.5 text-primary hover:underline"><Phone className="h-4 w-4" /> {detail.phone}</a>}
                </div>

                {detail.message && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Anschreiben</p>
                    <p className="whitespace-pre-wrap bg-muted/40 rounded-lg p-3">{detail.message}</p>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  {detail.cv_storage_path && (
                    <Button variant="outline" size="sm" asChild data-testid="download-cv">
                      <a href={recruitingAPI.cvUrl(detail.id)} target="_blank" rel="noreferrer">
                        <Download className="mr-2 h-4 w-4" /> Lebenslauf
                      </a>
                    </Button>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Bewertung</span>
                    <Stars value={detail.rating} onChange={canEdit ? (r) => setRating(detail, r) : undefined} />
                  </div>
                </div>

                {canEdit && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Status ändern</p>
                    <div className="flex flex-wrap gap-2">
                      {stages.map(s => (
                        <Button key={s} size="sm" variant={detail.status === s ? 'default' : 'outline'}
                          onClick={() => moveTo(detail, s)} data-testid={`detail-status-${s}`}>{labels[s]}</Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notizen */}
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Notizen</p>
                  <div className="space-y-2">
                    {(detail.notes || []).map(n => (
                      <div key={n.id} className="bg-muted/40 rounded-lg p-2">
                        <p className="whitespace-pre-wrap">{n.text}</p>
                        <p className="text-[11px] text-muted-foreground mt-1">{n.by_name} · {fmt(n.at)}</p>
                      </div>
                    ))}
                    {(detail.notes || []).length === 0 && <p className="text-xs text-muted-foreground/60">Keine Notizen</p>}
                  </div>
                  {canEdit && (
                    <div className="flex gap-2">
                      <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2} placeholder="Notiz hinzufügen…" data-testid="note-input" />
                      <Button size="sm" onClick={addNote} data-testid="add-note-btn">Add</Button>
                    </div>
                  )}
                </div>

                {canEdit && (
                  <div className="pt-2 border-t">
                    {detail.converted_employee_id ? (
                      <Button variant="outline" className="w-full" onClick={() => navigate(`/employees/${detail.converted_employee_id}`)} data-testid="goto-employee-btn">
                        <UserPlus className="mr-2 h-4 w-4" /> Zum Mitarbeiter (Anwärter)
                      </Button>
                    ) : (
                      <Button className="w-full" onClick={() => setConvertOpen(true)} data-testid="convert-btn">
                        <UserPlus className="mr-2 h-4 w-4" /> Als Anwärter übernehmen
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Übernahme-Bestätigung */}
      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent data-testid="convert-dialog">
          <DialogHeader><DialogTitle>Bewerber übernehmen</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Es wird ein Mitarbeiter mit Status <strong>Anwärter</strong> angelegt. Name, Kontaktdaten und der
            Lebenslauf werden übernommen – nichts geht verloren. Du kannst die Akte anschließend vervollständigen.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertOpen(false)}>Abbrechen</Button>
            <Button onClick={convert} disabled={converting} data-testid="convert-confirm-btn">
              {converting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Übernehmen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Empfehlungen verwalten */}
      <Dialog open={refOpen} onOpenChange={setRefOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="referrals-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Gift className="h-5 w-5 text-primary" /> Mitarbeiter werben Mitarbeiter</DialogTitle>
          </DialogHeader>

          {/* Prämie einstellen */}
          {canEdit && (
            <div className="flex items-end gap-3 p-3 bg-muted/40 rounded-lg">
              <div className="space-y-1.5 flex-1">
                <Label className="text-xs">Prämie pro erfolgreicher Einstellung ({refSettings.currency})</Label>
                <div className="relative">
                  <Euro className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input type="number" min="0" step="50" value={bonusInput}
                    onChange={(e) => setBonusInput(e.target.value)} className="pl-8" data-testid="bonus-input" />
                </div>
              </div>
              <Button onClick={saveSettings} disabled={savingSettings} data-testid="save-bonus-btn">
                {savingSettings ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Speichern
              </Button>
            </div>
          )}

          {/* Empfehlungsliste */}
          <div className="space-y-2">
            {referrals.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Noch keine Empfehlungen eingegangen.</p>
            ) : referrals.map(r => (
              <div key={r.id} className="flex items-center justify-between gap-3 border rounded-lg p-3" data-testid={`referral-row-${r.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{r.first_name} {r.last_name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {r.job_title} · geworben von <strong>{r.referred_by_name || '—'}</strong>
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {r.referral_bonus_status === 'ausgezahlt' ? (
                    <Badge variant="outline" className="text-emerald-600 border-emerald-300">ausgezahlt</Badge>
                  ) : r.referral_bonus_status === 'anspruch' ? (
                    <>
                      <Badge>{Number(r.bonus_amount || 0).toLocaleString('de-DE')} {r.currency}</Badge>
                      {canEdit && (
                        <Button size="sm" variant="outline" onClick={() => payout(r)} data-testid={`payout-${r.id}`}>
                          Auszahlen
                        </Button>
                      )}
                    </>
                  ) : (
                    <Badge variant="secondary">offen</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
